import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EbikLogo } from "@/components/EbikLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role: "customer" });
      login(data.token, data.user);
      toast.success("Company account created");
      nav("/app");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B0F17] text-white relative overflow-hidden grain">
        <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <Link to="/"><EbikLogo variant="dark" /></Link>
        <div className="relative z-10">
          <p className="eyebrow text-primary">Business Onboarding</p>
          <h1 className="mt-4 text-4xl font-heading font-extrabold leading-tight">Ship smarter across Southern Africa.</h1>
          <ul className="mt-6 space-y-3 text-slate-300 text-sm">
            {["Instant freight quotations", "Real-time shipment tracking", "Digital proof of delivery", "Consolidated invoicing"].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{t}</li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-xs font-mono text-slate-500">Enterprise-grade · ERP-ready · Pan-African</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><EbikLogo /></div>
          <h2 className="text-2xl font-heading font-extrabold">Register your company</h2>
          <p className="text-muted-foreground text-sm mt-1">Create a B2B account in under a minute.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div><Label>Full name</Label><Input data-testid="reg-name" value={form.name} onChange={set("name")} required className="mt-1.5" /></div>
            <div><Label>Company name</Label><Input data-testid="reg-company" value={form.company_name} onChange={set("company_name")} required className="mt-1.5" /></div>
            <div><Label>Work email</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Password</Label><Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5" /></div>
            <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-red-700 h-11">
              {loading ? "Creating…" : "Create Account"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">Already registered? <Link to="/login" className="text-primary font-semibold">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
